import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import { formatHolidayCount } from "common/holiday-calendar";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppSelectInput from "components/app-select-input";
import AppCalendarPlusIcon from "components/icons/app-calendar-plus-icon";
import AppImportIcon from "components/icons/app-import-icon";

const SOURCES = { EMPTY: "EMPTY", COPY: "COPY" };

export const AppHolidayCalendarYearModal = (props, ref) => {
	const dispatch = useDispatch();
	const [calendar, setCalendar] = useState();
	const [visible, setVisible] = useState(false);
	const years = useMemo(() => calendar?.years || [], [calendar]);
	const existingYears = useMemo(() => years.map((o) => o.year), [years]);
	/* Suggest the year after the latest one that exists, which is what an admin rolling a
	   calendar forward almost always wants. */
	const suggestedYear = useMemo(() => (existingYears.length ? Math.max(...existingYears) + 1 : new Date().getFullYear()), [existingYears]);
	const copyableYears = useMemo(() => years.filter((o) => o.holidays?.length), [years]);
	//prettier-ignore
	const sourceYearOptions = useMemo(() => copyableYears.map((o) => ({ label: o.year.toString() + " - " + formatHolidayCount(o.holidays?.length || 0), value: o.id.toString() })), [copyableYears]);
	//prettier-ignore
	const initialValues = useMemo(() => ({
		year: suggestedYear.toString(),
		source: SOURCES.EMPTY,
		sourceId: copyableYears.length ? copyableYears[0].id.toString() : "",
	}), [suggestedYear, copyableYears]);
	const formik = useFormik({
		enableReinitialize: true,
		initialValues,
		validationSchema: yup.object({
			year: yup
				.string()
				.required(ERRORS.REQUIRED)
				.matches(/^\d{4}$/, "Please enter a 4 digit year")
				.test("is-not-existing-year", "This calendar already has a year for that", function (value) {
					if (!value) return true;

					return !existingYears.includes(parseInt(value));
				}),
			sourceId: yup.string().when("source", {
				is: SOURCES.COPY,
				then: (schema) => schema.required(ERRORS.REQUIRED),
			}),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	const onHandleShow = useCallback((data) => {
		setCalendar(data);
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		formik.resetForm();
		setCalendar();
		setVisible(false);
	}, [formik]);

	//prettier-ignore
	const onHandleSelectSource = useCallback((source) => {
		formik.setFieldValue("source", source);
	}, [formik]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = null;
		const year = parseInt(values.year);

		try {
			if (values.source === SOURCES.COPY) {
				/* The duplicate endpoint exists for exactly this - clone a year of a calendar
				   into another year of the same region - so the copy semantics stay server side. */
				await api.post.holidayCalendar.duplicate({ id: values.sourceId, targetYear: year, title: calendar.title });
			} else {
				await api.post.holidayCalendar.create({ title: calendar.title, region: calendar.region, regionCode: calendar.regionCode, year, holidays: [] });
			}

			response = year;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: response + " has been added to this holiday calendar" }));
			props.onHandleGetList(response);
		}
	}, [dispatch, onHandleDismiss, calendar, formik, props]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	//prettier-ignore
	const SourceCard = useCallback(({ source, label, description, children, icon }) => {
		const selected = formik.values.source === source;
		const className = "holiday-calendar-year-modal__option" + (selected ? " holiday-calendar-year-modal__option--selected" : "");

		return (
			<div className={className}>
				<button type="button" className="holiday-calendar-year-modal__option-main" disabled={formik.isSubmitting} onClick={() => onHandleSelectSource(source)}>
					<div className="holiday-calendar-year-modal__option-icon">{icon}</div>

					<div className="holiday-calendar-year-modal__option-text">
						<p className="holiday-calendar-year-modal__option-label">{label}</p>
						<p className="holiday-calendar-year-modal__option-description">{description}</p>
					</div>
				</button>

				{selected && children}
			</div>
		);
	}, [formik.values.source, formik.isSubmitting, onHandleSelectSource]);

	return (
		<Modal classes={{ root: "app-holiday-calendar-year-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="holiday-calendar-year-modal" aria-describedby="holiday-calendar-year-modal">
			<div className="holiday-calendar-year-modal">
				<div className="holiday-calendar-year-modal__main">
					<div className="holiday-calendar-year-modal__header">
						<p className="holiday-calendar-year-modal__title">Add New Year</p>
						<p className="holiday-calendar-year-modal__description">
							{calendar?.title} · {calendar?.region || calendar?.regionCode}
						</p>
					</div>

					<form className="holiday-calendar-year-modal__body" onSubmit={formik.handleSubmit}>
						{/* prettier-ignore */}
						<AppInput required type="number" maxLength={4} name="year" label="Year" placeholder="e.g. 2027" value={formik.values.year} error={formik.errors.year} touched={formik.touched.year} disabled={formik.isSubmitting} onChange={formik.handleChange} />

						<div className="holiday-calendar-year-modal__options">
							<SourceCard source={SOURCES.EMPTY} label="Empty Calendar" description="Start with no holidays and add them one by one." icon={<AppCalendarPlusIcon color={formik.values.source === SOURCES.EMPTY ? "#0245a9" : "#666666"} />} />

							<SourceCard source={SOURCES.COPY} label="Copy from Previous Year" description="Bring over every holiday from a year that already exists." icon={<AppImportIcon color={formik.values.source === SOURCES.COPY ? "#0245a9" : "#666666"} />}>
								<div className="holiday-calendar-year-modal__option-field">
									{copyableYears.length ? (
										//prettier-ignore
										<AppSelectInput required searchable={false} name="sourceId" label="Copy From" placeholder="Select a year" options={sourceYearOptions} value={formik.values.sourceId} error={formik.errors.sourceId} touched={formik.touched.sourceId} disabled={formik.isSubmitting} onChange={formik.handleChange} />
									) : (
										<p className="holiday-calendar-year-modal__option-empty">There are no years with holidays to copy from yet.</p>
									)}
								</div>
							</SourceCard>
						</div>

						<div className="holiday-calendar-year-modal__button-container">
							<AppButton type="button" label="Cancel" outline disabled={formik.isSubmitting} onClick={onHandleDismiss} />
							<AppButton type="submit" label="Create Year" disabled={formik.isSubmitting || (formik.values.source === SOURCES.COPY && !copyableYears.length)} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppHolidayCalendarYearModal));

AppHolidayCalendarYearModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func.isRequired,
};
